import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Link as v4Link } from 'react-router-dom';
import { connect } from 'react-redux';
import promise from 'promise';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Container from '@zg-rentals/multibrand-ui/lib/Container';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import StaticMap from '@zg-rentals/multibrand-ui/lib/StaticMap';
import Tooltip from '@zg-rentals/multibrand-ui/lib/Tooltip';
import HelpCircle from '@zg-rentals/multibrand-ui/lib/icons/HelpCircle';
import LockClose from '@zg-rentals/multibrand-ui/lib/icons/LockClose';

import Page from 'app/shared/containers/Page';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

// actions
import AddressAction from 'app/shared/actions/AddressActions';
import StaticMapActions from 'app/shared/actions/StaticMapActions';

// constants
import PriceRentalsConstants from 'app/shared/constants/PriceRentalsConstants';

// selectors
import AppSelectors from 'app/shared/selectors/AppSelectors';
import AddressSelectors from 'app/shared/selectors/AddressSelectors';

// utils
import analyticsUtils from 'app/client/utils/analyticsUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import queryUtils from 'app/shared/utils/queryUtils';

// const
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

// ui
import zgBrands from 'app/shared/images/z-g-3-brands-color-r-g-b@2x.jpg';

import pathsManifest from 'app/shared/pathsManifest';

// Stylesheets
import './style.scss';

const logger = log.child({ name: 'react' });

class PriceRentalResultsPage extends React.Component {
    static contextTypes = {
        paths: PropTypes.object,
    };

    constructor(props) {
        super(props);

        // init state
        this.state = {
            compList: [],
            errorFound: false,
            errorMsg: '',
            hasComps: false,
            isLoading: true,
            priceRange: {
                low: 0,
                high: 0,
            },
        };

        this.fetchComps = this.fetchComps.bind(this);
        this.handleLoadComplete = this.handleLoadComplete.bind(this);

        // render methods
        this.renderCompList = this.renderCompList.bind(this);
        this.renderErrorMsg = this.renderErrorMsg.bind(this);
        this.renderPage = this.renderPage.bind(this);
        this.renderSimilarRentals = this.renderSimilarRentals.bind(this);
    }

    componentDidMount() {
        // deconstruct vars
        const { AddressDetailsModel, dispatch, queryString } = this.props;
        const { fullAddress, unitNumber } = queryUtils.parse(queryString);

        // if address details model populated
        if (AddressDetailsModel.zpid) {
            this.fetchComps(AddressDetailsModel.zpid);
        } else {
            // fetch address details
            dispatch(AddressAction.getDetails({ address: fullAddress, unit: unitNumber }))
                .then((AddressDetailsModel) => {
                    this.fetchComps(AddressDetailsModel.zpid);
                })
                .catch((errorRMWModel) => {
                    this.setState({
                        errorFound: true,
                        errorMsg: errorRMWModel.message,
                        isLoading: false,
                    });
                });
        }
    }

    fetchComps(zpid) {
        const { dispatch } = this.props;

        if (!zpid) {
            console.error('zpid is required @ fetchComps');

            this.setState({
                isLoading: false,
            });

            return;
        }

        dispatch(AddressAction.getComps(zpid))
            .then((response) => {
                const compList = response.comps;

                // calc price range
                let price = 0;
                let rentLow = 0;
                let rentHigh = 0;

                compList.forEach((item, i) => {
                    price = item.monthlyPayment;

                    if (i === 0) {
                        rentLow = price;
                    }

                    rentLow = Math.min(price, rentLow);
                    rentHigh = Math.max(price, rentHigh);
                });

                this.setState({
                    compList,
                    hasComps: compList.length > 0,
                    isLoading: false,
                    priceRange: {
                        low: rentLow,
                        high: rentHigh,
                    },
                });
            });
    }

    renderCompList() {
        const { compList } = this.state;
        const { AddressDetailsModel } = this.props;

        // loop through comps
        const similarRentalsHTML = compList.map((item, i) => {
            const primaryPhotoUrl = item.propertyPhotos[0].url;
            const price = `${numberUtils.asCurrency(item.monthlyPayment)}`;
            const address = `${item.streetAddress}, ${item.city}, ${item.state}`;

            const hdpLink = `https://www.zillow.com/homes/${item.zpid}_zpid`;

            const details =
                `${stringHelper.pluralize(item.bedrooms, 'bed', 'beds')} ·
                ${stringHelper.pluralize(item.bathrooms, 'bath', 'baths')} ·
                ${item.squareFeet} sqft`;

            const { livingArea: addressSquareFeet } = AddressDetailsModel;
            const diffSqft = `${Number(item.squareFeet) - Number(addressSquareFeet)}`;
            const diffSqftDisplay = diffSqft > 0 ? `+${diffSqft} sqft` : `${diffSqft} sqft`;
            const diffDistance = `${Math.round(item.distanceFromZpid * 100) / 100} miles away`;

            return (
                <div key={i} className="PriceRentalResultsPage-Comp-Item">
                    <Col lg={4} className="Comp-Item-Img">
                        <div className="Comp-ImgContainer">
                            <img className="Img" src={primaryPhotoUrl} alt="PriceRentalResultsPage-Comp-Item" />
                        </div>
                    </Col>

                    <Col className="Comp-Item-Details" lg={4}>
                        <div className="ForRent">
                            <div className="Inline-Element ForRent-Dot" />
                            <Text className="Inline-Element ForRent-Title" type="body-small">for rent</Text>
                        </div>
                        <Text type="h2">{price}</Text>
                        <Text type="body">
                            <Link to={hdpLink} target="_blank">{address}</Link>
                            <div>{details}</div>
                        </Text>
                    </Col>

                    <Col className="Comp-Item-Diff" lg={2}>
                        <div
                            className={cx({
                                green: diffSqft > 0,
                                red: diffSqft < 0,
                            })}
                        >
                            {diffSqftDisplay}
                        </div>
                        <Text type="body">{diffDistance}</Text>
                    </Col>
                </div>
            );
        });


        return similarRentalsHTML;
    }

    renderSimilarRentals() {
        const { hasComps } = this.state;

        const title = 'Similar Rentals';
        const renderCompList = this.renderCompList();
        const noResultsTitle = 'No comparable listings found';

        return (
            <Row className="PriceRentalResultsPage-Comps-Row">
                <Col>
                    <Text type="h3">{title}</Text>

                    {hasComps ? (
                        <div className="PriceRentalResultsPage-Comp-List">
                            {renderCompList}
                        </div>
                    ) : (
                        <Text type="body">{noResultsTitle}</Text>
                    )}
                </Col>
            </Row>
        );
    }

    signUrl = (url, brand) => {
        const { isDev } = this.props;
        const params = { brandName: brand };

        if (isDev) {
            return promise.resolve(url, brand);
        }
        return this.props.dispatch(StaticMapActions.signUrl(url, params))
            .catch(err => logger.error(err));
    };

    renderErrorMsg() {
        const { errorMsg } = this.state;

        return (
            <Page noPadding={true} className="PriceRentalResultsPage-Content">
                <Container className="PriceRentalResultsPage-Container Container-ZSG-Breakpoints">
                    <Row className="PriceRentalResultsPage-Error-Row">
                        <Col>
                            <Text type="h3">{errorMsg}</Text>
                        </Col>
                    </Row>
                </Container>
            </Page>
        );
    }

    renderStaticMap = () => {
        const { compList, hasComps } = this.state;
        const { AddressDetailsModel } = this.props;

        const width = 2048;
        const height = 400;
        const zoom = 13;

        // list of marker groups
        const markers = [];

        if (hasComps) {
            // populate comp locations
            const locations = [];
            compList.forEach((item) => {
                locations.push({
                    lat: item.latitude,
                    lng: item.longitude,
                });
            });

            // set marker locations
            markers.push({
                icon: 'primary',
                locations,
            });
        }

        // use address details location to center
        // otherwise use default - zillow group in seattle, wa
        const centerLat = AddressDetailsModel.geo ? AddressDetailsModel.geo.lat : 47.6072514;
        const centerLng = AddressDetailsModel.geo ? AddressDetailsModel.geo.lon : -122.3387425;

        // static map will not render locally
        return (
            <StaticMap
                className="PriceRentalResultsPage-StaticMap"
                signUrl={this.signUrl}
                lat={centerLat}
                lng={centerLng}
                height={height}
                width={width}
                markers={markers}
                zoom={zoom}
            />
        );
    };

    renderPropertyDetails() {
        const { hasComps, priceRange } = this.state;
        const { AddressDetailsModel } = this.props;
        const paths = pathsManifest.getAllPaths();

        const {
            beds, baths, livingArea, rentZestimate, streetAddress,
        } = AddressDetailsModel;
        const hasRentZestimate = rentZestimate > 0;

        const { street, city, state } = streetAddress;

        // format
        const cityState = `${city}, ${state}`;

        const rentZestimateTitle = 'Rent Zestimate';
        const rentZestimateAsCurrency = `${numberUtils.asCurrency(rentZestimate)}`;
        const details =
            `${stringHelper.pluralize(beds, 'bed', 'beds')} •
            ${stringHelper.pluralize(baths, 'bath', 'baths')} •
            ${livingArea} sqft`;

        // comp summary
        const compTitle = 'Homes like this rent for';
        const compPriceRange =
            `${numberUtils.asCurrency(priceRange.low)}-${numberUtils.asCurrency(priceRange.high)}`;

        return (
            <Row className="PriceRentalResultsPage-AddressDetails-Row">
                <Col lg={8} className="PriceRentalResultsPage-AddressDetails">
                    <Text type="h1">{street}</Text>
                    <Text type="h2">{cityState}</Text>
                    <Text type="h4">
                        <div className="Inline-Element">{details}</div>
                        {/* <Link
                            className="Inline-Element"
                            reactRouterLink={v4Link}
                            to={paths.propertiesPage}
                        >
                            <Text type="h4">Edit</Text>
                        </Link>*/}
                    </Text>

                    {
                        hasRentZestimate &&
                        (
                            <Text type="body">
                                <div className="Inline-Element">{rentZestimateTitle}</div>

                                <Tooltip
                                    targetClass="Tooltip-story-target"
                                    className="Inline-Element"
                                    trigger="click"
                                >
                                    <Tooltip.Target>
                                        <HelpCircle
                                            size={13}
                                            className="PriceRentalResultsPage-tooltip-icon Inline-Element"
                                        />
                                    </Tooltip.Target>

                                    <Text type="body">
                                        A Rent Zestimate® is Zillow&apos;s estimated monthly rental price,
                                        computed using a proprietary formula. It is a starting point in
                                        determining the monthly rental price for a specific property.
                                    </Text>

                                    <Text
                                        className="PriceRentalResultsPage-tooltip-link"
                                        type="body"
                                    >
                                        <Link
                                            target="_blank"
                                            reactRouterLink={v4Link}
                                            to={paths.zestimatePage}
                                        >
                                            Learn more about Zestimates.
                                        </Link>
                                    </Text>
                                </Tooltip>

                                <div className="Inline-Element">{rentZestimateAsCurrency}</div>
                            </Text>
                        )
                    }
                </Col>

                <Col lg={4} className="PriceRentalResultsPage-CompRange">
                    {hasComps && (
                        <React.Fragment>
                            <Text type="h4">{compTitle}</Text>
                            <Text type="h3">{compPriceRange}</Text>
                        </React.Fragment>
                    )}
                </Col>
            </Row>
        );
    }

    triggerGA() {
        const { hasComps } = this.state;
        const {
            brand, AddressDetailsModel, dispatch, queryString,
        } = this.props;
        const { rentZestimate } = AddressDetailsModel;
        const hasRentZestimate = rentZestimate > 0;
        const hasZpid = Boolean(AddressDetailsModel.zpid);
        const { fullAddress, unitNumber } = queryUtils.parse(queryString);
        let event = {};

        // set dimension
        const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
        let dimensionValue = fullAddress;
        if (unitNumber) {
            const [street, ...remainingAddress] = fullAddress.split(',');
            dimensionValue = [`${street} #${unitNumber}`, ...remainingAddress].join(',');
        }
        analyticsUtils.setCustomDimension(dimensionValue, dimension, brand);

        // check comps
        if (hasComps) {
            event = hasRentZestimate ?
                PriceRentalsConstants.GA_EVENTS.WITH_COMPS_WITH_ZESTIMATE :
                PriceRentalsConstants.GA_EVENTS.WITH_COMPS_WITH_OUT_ZESTIMATE;
        } else {
            event = PriceRentalsConstants.GA_EVENTS.CANNOT_FIND_COMPS;
        }

        // trigger ga for comps
        dispatch(analyticsUtils.track.GAEventV2(event));

        // check zpid
        if (!hasZpid) {
            event = PriceRentalsConstants.GA_EVENTS.CANNOT_FIND_ADDRESS;

            // trigger ga for zpid
            dispatch(analyticsUtils.track.GAEventV2(event));
        }
    }

    handleLoadComplete() {
        const { errorFound } = this.state;

        if (!errorFound) {
            this.triggerGA();
        }

        return errorFound ? this.renderErrorMsg() : this.renderPage();
    }

    renderPage() {
        const { zpid } = this.props.AddressDetailsModel;
        const createFreeRentalURL = 'https://www.zillow.com/rental-manager/properties?postingPath=true';
        const targetCreateFreeRental = '_blank';
        const claimHomeURL = `https://www.zillow.com/homedetails/${zpid}_zpid/?view=public`;
        const targetClaimHome = '_blank';

        return (
            <React.Fragment>
                <div className="PriceRentalResultsPage-Map">
                    {this.renderStaticMap()}
                </div>

                <Page noPadding={true} className="PriceRentalResultsPage-Content">
                    <Container className="PriceRentalResultsPage-Container Container-ZSG-Breakpoints">
                        <Row>
                            <Col lg={8} className="PriceRentalResultsPage-AddressDetails-Col">
                                {this.renderPropertyDetails()}
                                {this.renderSimilarRentals()}
                            </Col>

                            <Col lg={4} className="PriceRentalResultsPage-Action-Col">
                                <div className="PriceRentalResultsPage-CreateListing">
                                    <Text className="CreateListing-Text" type="h4">Add Details, Photos and More.</Text>
                                    <Link href={createFreeRentalURL} target={targetCreateFreeRental}>
                                        <Button className="CreateListing-Button" type="primary">
                                            Create your rental listing
                                        </Button>
                                    </Link>
                                </div>
                                <div className="PriceRentalResultsPage-Info">
                                    <div className="ImgContainer">
                                        <img className="Img" src={zgBrands} alt="PriceRentalResultsPage-Info" />
                                    </div>

                                    <Text type="body">
                                        Your listing will be seen on Zillow, Trulia and HotPads—the largest
                                        rental network on the web.
                                    </Text>
                                </div>

                                <div className="PriceRentalResultsPage-ClaimListing">
                                    <div className="ClaimListing-Content-Container">
                                        <LockClose className="Inline-Element" size={24} />

                                        <div>
                                            <Text className="ClaimListing-Content" type="body">
                                                Are you the owner?
                                                Claim your home to edit beds, baths and square footage.
                                            </Text>

                                            <Text type="body">
                                                <Link href={claimHomeURL} target={targetClaimHome}>Claim Home</Link>
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </Page>
            </React.Fragment>
        );
    }

    render() {
        const pageTitle = 'Rent Comp Estimator Results';

        return (
            this.state.isLoading ? <SpinnerContainer /> : (
                <div className="PriceRentalResultsPage">
                    <HeadWrapper
                        title={pageTitle}
                        noIndex={true}
                    />

                    {this.handleLoadComplete()}
                </div>
            )
        );
    }
}

const mapStateToProps = state => ({
    brand: state.app.brand,
    isDev: AppSelectors.makeIsDev(state),
    AddressDetailsModel: AddressSelectors.makeAddressDetails(state),
    queryString: state.router.location.search,
});

export { PriceRentalResultsPage };
export default connect(mapStateToProps)(PriceRentalResultsPage);
